import React, { useState } from 'react';
import ImportCSVFile from './ImportCSVFile';
import BatchLoadExperimentWizardContext, {
  EBatchloadDirectoryType,
} from './BatchLoadExperimentWizardContext';
import {
  BatchLoadExperimentsData,
  BatchLoadExperimentsDTO,
} from 'business/models/Models';
import PreviewDataBeforeExperimentCreation from './PreviewDataBeforeExperimentCreation';
import CreateBatchLoadExperiments from './CreateBatchLoadExperiments';
import { FmlxLink, FmlxModal, FmlxWizard } from 'fmlx-common-ui';
import { EXTERNAL_LINKS } from 'constants/externalLinks';
import { RMLink } from 'components/customComponents/RMLink';

enum WizardSteps {
  ImportCSVFile = 0,
  PreviewDataBeforeExperimentCreation = 1,
  CreateBatchLoadExperiments = 2,
}

function BatchloadExperimentWizard({ onClose }: any) {
  const [nextButtonDisallowed, setNextButtonDisallowed] = useState(true);

  const [batchLoadExperimentsDTO, setBatchLoadExperimentsDTO] =
    useState<BatchLoadExperimentsDTO>(undefined);

  const [selectedStatus, setSelectedStatus] = useState(0);

  const [fileName, setFileName] = useState('');

  const [fileSize, setFileSize] = useState('');

  const [expNameFormatString, setExpNameFormatString] = useState(
    '<PlateBarcode><Screen><Temperature>',
  );

  const [experimentData, setExperimentData] = useState<BatchLoadExperimentsData[]>([]);

  const [folderHierarchyOrder, setFolderHierarchyOrder] = useState<
    EBatchloadDirectoryType[]
  >([
    EBatchloadDirectoryType.FOLDER,
    EBatchloadDirectoryType.PROJECT,
    EBatchloadDirectoryType.SUBFOLDER,
  ]);

  const [activeStep, setActiveStep] = useState(WizardSteps.ImportCSVFile);
  const [openState, setOpenState] = useState(true);

  const steps = [
    {
      id: WizardSteps.ImportCSVFile,
      stepper: {
        title: 'Import CSV File',
        desc: 'Please import the csv file',
        disabled: nextButtonDisallowed,
        error: false,
      },
      content: <ImportCSVFile />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        // code to move to the previous step
      },
    },
    {
      id: WizardSteps.PreviewDataBeforeExperimentCreation,
      stepper: {
        title: 'REVIEW EXPERIMENT(S)',
        desc: 'Please review the experiment data',
        disabled: nextButtonDisallowed,
        error: false,
      },
      content: <PreviewDataBeforeExperimentCreation />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.CreateBatchLoadExperiments,
      stepper: {
        title: 'CREATE EXPERIMENT(S)',
        desc: 'Please create the experiment(s)',
        disabled:
          nextButtonDisallowed ||
          activeStep !== WizardSteps.PreviewDataBeforeExperimentCreation,
        error: false,
      },
      content: <CreateBatchLoadExperiments onClose={onClose} />,
      nextStepCallback: () => {
        // code to move to the next step
      },
      backStepCallback: () => {
        // code to move to the previous step
      },
    },
  ];

  const handleCloseClick = () => {
    onClose();
    setOpenState(false);
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleCancelClick = () => {
    setActiveStep(0);
  };

  const handleSkipClick = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevState) => prevState + 1);
    }
  };

  const wizardSubtitle = (
    <div className="flex-col" data-testid="batchload-wizard-subtitle">
      <div className="mb-1">Create Batchload Experiment(s)</div>
      <div className="flex-row">
        <span className="!font-normal">Learn more about </span>
        <RMLink
          id="batchload-wizard-rmlink"
          text="Batchload Experiment Wizard"
          url={EXTERNAL_LINKS.BATCHLOAD_WIZARD_WEBHELP}
          customLinkSize={FmlxLink.Size.SMALL}
          customIconSize={12}
          customColor="#f7a246"
        />
      </div>
    </div>
  );

  return (
    <BatchLoadExperimentWizardContext.Provider
      value={{
        setNextButtonDisallowed,
        batchLoadExperimentsDTO,
        setBatchLoadExperimentsDTO,
        fileName,
        setFileName,
        fileSize,
        setFileSize,
        selectedStatus,
        setSelectedStatus,
        expNameFormatString,
        setExpNameFormatString,
        experimentData,
        setExperimentData,
        folderHierarchyOrder,
        setFolderHierarchyOrder,
      }}
    >
      <FmlxWizard
        id="batchloadexperimentwizard"
        title="Batchload Experiment Wizard"
        subtitle={wizardSubtitle as any}
        steps={steps}
        activeStep={activeStep}
        open={openState}
        onCloseClick={handleCloseClick}
        onStepClick={handleStepClick}
        onCancelClick={handleCancelClick}
        onSkipClick={handleSkipClick}
        onFinishClick={handleCloseClick}
        disableNextButton={nextButtonDisallowed}
        type={FmlxWizard.Type.MODAL}
        modalSize={FmlxModal.Size.DOUBLE_EXTRA_LARGE}
      />
    </BatchLoadExperimentWizardContext.Provider>
  );
}

export default BatchloadExperimentWizard;
